import { useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const from = useLocation().state?.from || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to={from} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email, password);
      nav(from);
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden lg:flex flex-col justify-between bg-[#0E2D43] text-white p-12 overflow-hidden grain">
        <div className="absolute -right-24 -bottom-24 w-[520px] h-[520px] rounded-full border-[60px] border-[#F49619]/90" />
        <div className="absolute right-40 bottom-40 w-40 h-40 rounded-full bg-white/5" />
        <img src="/logo-presthyg.png" alt="Prest'hyg" className="relative w-52 h-auto -ml-3 mix-blend-lighten" data-testid="login-logo" />
        <div className="relative max-w-md">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#F49619] font-bold mb-4">Application interne</div>
          <h1 className="font-display text-5xl font-extrabold leading-[1.05]">Gestion de flotte</h1>
          <p className="mt-5 text-white/70 leading-relaxed">
            Contrôles des véhicules de livraison, relevés kilométriques et suivi des dépenses carburant des véhicules commerciaux.
          </p>
        </div>
        <div className="relative text-xs text-white/40">© Prest'hyg — usage interne</div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-[#F4F6F9]">
        <form onSubmit={submit} className="w-full max-w-sm rise" data-testid="login-form">
          <div className="lg:hidden mb-10 rounded-xl bg-[#0E2D43] flex justify-center py-4">
            <img src="/logo-presthyg.png" alt="Prest'hyg" className="w-40 h-auto mix-blend-lighten" data-testid="login-logo-mobile" />
          </div>
          <h2 className="font-display text-3xl font-bold text-[#0E2D43]">Connexion</h2>
          <p className="text-sm text-slate-500 mt-2 mb-8">Les comptes sont créés par la direction.</p>
          <label className="block text-[11px] uppercase font-bold tracking-wider text-slate-500 mb-1.5">Email</label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 bg-white mb-4" data-testid="login-email-input" autoComplete="email" />
          <label className="block text-[11px] uppercase font-bold tracking-wider text-slate-500 mb-1.5">Mot de passe</label>
          <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 bg-white" data-testid="login-password-input" autoComplete="current-password" />
          {err && <div className="mt-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="login-error">{err}</div>}
          <p className="mt-3 text-xs text-slate-500" data-testid="login-forgot-hint">Mot de passe oublié ? Contactez l'administrateur, qui vous communiquera un mot de passe provisoire.</p>
          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit-button"
            className="mt-6 w-full h-12 rounded-md bg-[#F49619] hover:bg-[#E0830B] text-[#0E2D43] font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Se connecter <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      </div>
    </div>
  );
}
